import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Box,
    TextField,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Checkbox,
    FormControlLabel,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Skeleton,
    Stack,
    InputAdornment,
} from '@mui/material';
import AccountEdit from "./AccountEdit";
import UserAvatar from "./UserAvatar";
import { useGetGroups, useGetUsers, useUpdateUser, useGetUserById } from '../hooks/users';
import { accountSchema, IAccount } from '../Interfaces/IAccount';
import { useTheme } from '@mui/material/styles';
import { ErrorTryRefetch } from '../core/ErrorTryRefetch';
import { GroupChip } from './GroupChip';
import { SaveButton } from '../core/SaveButton';
import { checkPermission, isSuperadmin } from "../utils/permission";
import { canImpersonate } from "../utils/canImpersonate";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDetailRoute } from '../hooks/useDetailRoute';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../core/AuthProvider';
import { useAlert } from '../core/AlertContext';
import { useStartImpersonation } from '../hooks/impersonation';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SearchIcon from '@mui/icons-material/Search';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { GREEN_700, GREEN_800, GREEN_900, cardSx, primaryButtonSx } from '../core/pvtaStyles';

const avatarSx = (size: number) => ({
    width: size,
    height: size,
    fontWeight: 700,
    bgcolor: GREEN_700,
    color: '#ffffff',
    border: '3px solid #ffffff',
    boxShadow: '0 8px 18px rgba(20, 35, 25, 0.16)',
    flexShrink: 0,
    '& img': { objectFit: 'cover' },
}) as const;

const GROUP_OPTIONS: { id: number; label: string }[] = [
    { id: 1, label: 'Präsid' },
    { id: 2, label: 'AH-Vorstand' },
    { id: 3, label: 'Escheck-Team' },
    { id: 4, label: 'Gäste' },
];

export const AccountsOverview: React.FC = () => {
    const { userArray, isLoadingUserArray, refetchUserArray } = useGetUsers();
    const { groupArray, isLoadingGroupArray, refetechGroupArray } = useGetGroups();
    const { updateAccount, isPending } = useUpdateUser();

    const [search, setSearch] = useState<string>('');
    const [filterVerstorben, setFilterVerstorben] = useState<boolean>(false);
    const [filterGroups, setFilterGroups] = useState<number[]>([]);
    // Nur fuer den noch nicht gespeicherten "Neuer Account" — der hat keine ID und
    // damit keine URL; alle bestehenden Accounts haengen an der Route.
    const [draftAccount, setDraftAccount] = useState<IAccount | null>(null);
    const [searchParams] = useSearchParams();
    const { detailId, hasInvalidId, openDetail, closeDetail } = useDetailRoute('/accounts');

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const currentUserId = Number(localStorage.getItem('user_id'));

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { applyAuthSession } = useAuth();
    const { showAlert } = useAlert();
    const { startImpersonation, isStarting } = useStartImpersonation();

    const handleImpersonate = async () => {
        if (!selectedAccount) {
            return;
        }
        try {
            const payload = await startImpersonation({ userId: selectedAccount.id });
            // Identitätswechsel-Reihenfolge: laufende Anfragen abbrechen, neue
            // Identität setzen, Cache leeren, DANN unter neuer Identität laden.
            await queryClient.cancelQueries();
            applyAuthSession(payload);
            queryClient.clear();
            navigate('/dashboard');
        } catch {
            showAlert({ severity: 'error', text: 'Impersonation konnte nicht gestartet werden.' });
        }
    };

    const listAccount = detailId === null
        ? null
        : (userArray?.find((user) => user.id === detailId) ?? null);

    // Die Liste ist serverseitig unpaginiert und deckt den Normalfall ab. Fehlt der
    // Datensatz dort trotzdem, fragen wir den Detail-Endpunkt — der ist allerdings
    // strenger (`IsSelfOrAdmin`) und antwortet fuer fremde Profile mit 403.
    const { user: fetchedAccount, isFetchedUser, isErrorUser } = useGetUserById(detailId, {
        enabled: detailId !== null && !isLoadingUserArray && listAccount === null,
    });

    const selectedAccount = draftAccount
        ?? (detailId === null ? null : (listAccount ?? fetchedAccount));
    const open = selectedAccount !== null;

    const handleCardClick = (user: IAccount) => {
        openDetail(user.id);
    };

    /**
     * Schliesst den Dialog: verwirft einen Entwurf, sonst zurueck auf die Uebersicht.
     * Absolut statt `navigate(-1)` — wer den Link aus einer Mail oeffnet, hat keinen
     * App-Verlauf und wuerde mit einem Schritt zurueck die Anwendung verlassen.
     */
    const handleClose = () => {
        if (draftAccount) {
            setDraftAccount(null);
            return;
        }
        closeDetail();
    };

    const toggleGroup = (groupId: number) => {
        setFilterGroups(prev =>
            prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
        );
    };

    const lowerSearch = search.toLowerCase().trim();
    const terms = lowerSearch ? lowerSearch.split(/\s+/) : [];

    const filteredUsers = (userArray || []).filter(user => {
        const haystacks = [
            user.first_name || '',
            user.last_name || '',
            user.strasse || '',
            user.plz || '',
            user.stadt || '',
            user.email || '',
            user.mobil || '',
            user.telefon || '',
            `${user.first_name || ''} ${user.last_name || ''}`,
        ].map(s => s.toLowerCase());

        const matchesSearch =
            !lowerSearch || terms.every(term => haystacks.some(field => field.includes(term)));

        const matchesVerstorben = filterVerstorben ? Boolean(user.verstorben) : !user.verstorben;

        const matchesGroup =
            filterGroups.length === 0 || user.customGroups.some(g => filterGroups.includes(g));

        return matchesSearch && matchesVerstorben && matchesGroup;
    });

    const activeFilterCount = filterGroups.length + (filterVerstorben ? 1 : 0);

    const handleAddNew = () => {
        // Eine offene Detail-Route mitnehmen, sonst taucht das alte Profil nach dem
        // Abbrechen wieder auf.
        if (detailId !== null) {
            closeDetail();
        }
        const emptyAccount = accountSchema.parse({ id: 0 });
        setDraftAccount(emptyAccount as IAccount);
    };

    // Altlast: Links der Form `/accounts?userId=42` (frueher aus Suche und
    // Benachrichtigungen) auf die neue Route umbiegen, damit bereits verschickte
    // Links weiter funktionieren.
    useEffect(() => {
        const legacyUserId = searchParams.get('userId');
        if (!legacyUserId) {
            return;
        }
        const parsedId = Number(legacyUserId);
        if (!Number.isSafeInteger(parsedId) || parsedId <= 0) {
            navigate('/accounts', { replace: true });
            return;
        }
        navigate(`/accounts/${parsedId}`, { replace: true });
    }, [navigate, searchParams]);

    // Deep-Link auf eine unbekannte ID oder ein Profil ohne Zugriff: zurueck auf
    // die Uebersicht statt eines leeren Dialogs.
    const detailUnavailable = detailId !== null
        && !isLoadingUserArray
        && listAccount === null
        && (isErrorUser || (isFetchedUser && fetchedAccount === null));

    useEffect(() => {
        if (!hasInvalidId && !detailUnavailable) {
            return;
        }
        showAlert({
            severity: 'error',
            text: 'Dieser Bundesbruder existiert nicht oder ist nicht für dich einsehbar.',
        });
        closeDetail();
    }, [closeDetail, detailUnavailable, hasInvalidId, showAlert]);

    const getInitials = (user: IAccount): string =>
        `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`;

    const renderBadges = (user: IAccount, fontSize: string) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {user.customGroups.map((group) => (
                <GroupChip key={group} idGroup={group} groupArray={groupArray ?? []} fontSize={fontSize} height={22} />
            ))}
        </Box>
    );

    const renderAddress = (user: IAccount) => {
        const line1 = [user.strasse, user.hausnummer].filter(Boolean).join(' ').trim();
        const line2 = [user.plz, user.stadt].filter(Boolean).join(' ').trim();
        if (!line1 && !line2) {
            return (
                <Typography variant="body2" sx={{ color: 'var(--pvta-muted)' }}>
                    Keine Adresse hinterlegt
                </Typography>
            );
        }
        return (
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <PlaceOutlinedIcon fontSize="small" sx={{ color: 'var(--pvta-muted)', mt: 0.25 }} />
                <Box sx={{ minWidth: 0 }}>
                    {line1 && (
                        <Typography variant="body2" sx={{ color: '#4f5b52', lineHeight: 1.35 }}>
                            {line1}
                        </Typography>
                    )}
                    {line2 && (
                        <Typography variant="body2" sx={{ color: '#4f5b52', lineHeight: 1.35 }}>
                            {line2}
                        </Typography>
                    )}
                </Box>
            </Stack>
        );
    };

    const renderContact = (user: IAccount) => {
        const phones = [user.mobil, user.telefon].filter((value): value is string => Boolean(value));
        if (!user.email && phones.length === 0) {
            return null;
        }
        return (
            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                {user.email && (
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <MailOutlineIcon fontSize="small" sx={{ color: GREEN_700, mt: 0.25, flexShrink: 0 }} />
                        <Typography
                            variant="body2"
                            sx={{ color: '#2f5d3a', fontWeight: 500, lineHeight: 1.35, overflowWrap: 'anywhere', minWidth: 0 }}
                        >
                            {user.email}
                        </Typography>
                    </Stack>
                )}
                {phones.map((phone) => (
                    <Stack key={phone} direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <PhoneOutlinedIcon fontSize="small" sx={{ color: 'var(--pvta-muted)', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: '#4f5b52', lineHeight: 1.35 }}>
                            {phone}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        );
    };

    const renderMemberCard = (user: IAccount) => (
        <Box
            role="button"
            tabIndex={0}
            onClick={() => handleCardClick(user)}
            onKeyDown={(keyboardEvent) => {
                if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                    keyboardEvent.preventDefault();
                    handleCardClick(user);
                }
            }}
            sx={{
                ...cardSx,
                p: { xs: 2, md: 2.5 },
                cursor: 'pointer',
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'var(--pvta-shadow-hover)',
                },
                '&:hover .pvta-card-chevron': { opacity: 1, transform: 'translateX(0)' },
            }}
        >
            {isDesktop ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.1fr) minmax(0, 1.5fr) auto',
                        gap: 3,
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                        <UserAvatar
                            userId={user.id}
                            avatarUrl={user.avatar_url}
                            hasAvatar={user.has_avatar}
                            initials={getInitials(user)}
                            sx={avatarSx(72)}
                        />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 16, color: 'var(--pvta-text)', mb: 0.75 }} noWrap>
                                {user.first_name} {user.last_name}
                            </Typography>
                            {renderBadges(user, '11px')}
                        </Box>
                    </Stack>
                    {renderAddress(user)}
                    {renderContact(user)}
                    <ChevronRightRoundedIcon
                        className="pvta-card-chevron"
                        sx={{
                            color: 'var(--pvta-muted)',
                            opacity: 0,
                            transform: 'translateX(-4px)',
                            transition: 'opacity 160ms ease, transform 160ms ease',
                        }}
                    />
                </Box>
            ) : (
                <Stack spacing={1.75}>
                    <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <UserAvatar
                            userId={user.id}
                            avatarUrl={user.avatar_url}
                            hasAvatar={user.has_avatar}
                            initials={getInitials(user)}
                            sx={avatarSx(60)}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 16, color: 'var(--pvta-text)', mb: 0.5 }} noWrap>
                                {user.first_name} {user.last_name}
                            </Typography>
                            {renderBadges(user, '11px')}
                        </Box>
                    </Stack>
                    <Box sx={{ pt: 1.5, borderTop: '1px solid var(--pvta-border)', display: 'grid', gap: 1.25 }}>
                        {renderAddress(user)}
                        {renderContact(user)}
                    </Box>
                </Stack>
            )}
        </Box>
    );

    if (isLoadingUserArray || isLoadingGroupArray) {
        return (
            <Box sx={{ py: { xs: 4, md: 5 } }}>
                <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                    <Stack spacing={3}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                                <Skeleton variant="text" width={240} height={48} />
                                <Skeleton variant="text" width={320} height={24} />
                            </Box>
                            <Skeleton variant="rounded" width={isMobile ? 44 : 190} height={44} sx={{ borderRadius: 999 }} />
                        </Box>
                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 'var(--pvta-radius-md)' }} />
                        {[0, 1, 2, 3].map((item) => (
                            <Skeleton key={item} variant="rounded" height={112} sx={{ borderRadius: 'var(--pvta-radius-md)' }} />
                        ))}
                    </Stack>
                </Container>
            </Box>
        );
    }

    if (!userArray || !groupArray) {
        return (
            <ErrorTryRefetch
                refetchFunction={() => {
                    refetchUserArray();
                    refetechGroupArray();
                }}
            />
        );
    }

    return (
        <Box sx={{ py: { xs: 4, md: 5 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                {/* Kopfbereich */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: { xs: 3, md: 4 },
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: 'Georgia, "Times New Roman", serif',
                                fontWeight: 700,
                                fontSize: { xs: 28, md: 36 },
                                lineHeight: 1.1,
                                color: GREEN_900,
                            }}
                        >
                            Bundesbrüder
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'var(--pvta-muted)', mt: 0.75 }}>
                            Finde Bundesbrüder, Kontaktdaten und Rollen innerhalb der Verbindung.
                        </Typography>
                    </Box>
                    {checkPermission(1, 2) && (
                        isMobile ? (
                            <Tooltip title="Neuer Bundesbruder">
                                <IconButton onClick={handleAddNew} sx={{ ...primaryButtonSx, px: 0, width: 44, height: 44, flexShrink: 0 }}>
                                    <PersonAddAlt1Icon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Button onClick={handleAddNew} startIcon={<PersonAddAlt1Icon />} sx={{ ...primaryButtonSx, flexShrink: 0 }}>
                                Neuen Bundesbruder
                            </Button>
                        )
                    )}
                </Box>

                {/* Such- und Filter-Card */}
                <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, mb: 3 }}>
                    <TextField
                        placeholder="Suche"
                        variant="outlined"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: 'var(--pvta-muted)' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 'var(--pvta-radius-sm)' },
                            '& .MuiOutlinedInput-input': { py: 1.5 },
                        }}
                    />

                    <Accordion
                        disableGutters
                        elevation={0}
                        defaultExpanded={isDesktop}
                        sx={{
                            mt: 1.5,
                            backgroundColor: 'transparent',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ px: 0, minHeight: 0, '& .MuiAccordionSummary-content': { my: 1 } }}
                        >
                            <Typography sx={{ fontWeight: 600, color: 'var(--pvta-text)' }}>
                                {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0 }}>
                            <Stack spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filterVerstorben}
                                            onChange={(e) => setFilterVerstorben(e.target.checked)}
                                            sx={{ color: GREEN_700, '&.Mui-checked': { color: GREEN_700 } }}
                                        />
                                    }
                                    label="Verstorbene anzeigen"
                                />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {GROUP_OPTIONS.map((group) => {
                                        const active = filterGroups.includes(group.id);
                                        return (
                                            <Chip
                                                key={group.id}
                                                label={group.label}
                                                onClick={() => toggleGroup(group.id)}
                                                variant={active ? 'filled' : 'outlined'}
                                                sx={{
                                                    borderRadius: 999,
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    backgroundColor: active ? GREEN_700 : 'transparent',
                                                    color: active ? '#ffffff' : 'var(--pvta-text)',
                                                    borderColor: active ? GREEN_700 : 'var(--pvta-border)',
                                                    '&:hover': {
                                                        backgroundColor: active ? GREEN_800 : 'var(--pvta-green-100)',
                                                    },
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                {/* Mitgliederliste */}
                <Stack spacing={2}>
                    {filteredUsers.length === 0 ? (
                        <Box sx={{ ...cardSx, p: 4, textAlign: 'center' }}>
                            <Typography sx={{ color: 'var(--pvta-muted)' }}>
                                Keine Bundesbrüder für die aktuelle Auswahl gefunden.
                            </Typography>
                        </Box>
                    ) : (
                        filteredUsers.map((user) => (
                            <Box key={user.id}>{renderMemberCard(user)}</Box>
                        ))
                    )}
                </Stack>

                {/* Bearbeiten Dialog */}
                <Dialog
                    open={open}
                    onClose={handleClose}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={{
                        sx: {
                            borderRadius: isMobile ? 0 : 'var(--pvta-radius-lg)',
                            backgroundColor: 'var(--pvta-bg)',
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: { xs: 2, md: 3 },
                            pt: { xs: `calc(env(safe-area-inset-top) + 12px)`, md: 3 },
                            pb: 1.5,
                            backgroundColor: 'var(--pvta-bg)',
                            borderBottom: '1px solid var(--pvta-border)',
                        }}
                    >
                        {isMobile && (
                            <IconButton
                                onClick={handleClose}
                                aria-label="Zurück"
                                sx={{ ml: -1, color: GREEN_900 }}
                            >
                                <ArrowBackRoundedIcon />
                            </IconButton>
                        )}
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: 'Georgia, "Times New Roman", serif',
                                fontWeight: 700,
                                fontSize: { xs: 22, md: 26 },
                                lineHeight: 1.1,
                                color: GREEN_900,
                            }}
                        >
                            Account bearbeiten
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2.5, px: { xs: 1.5, md: 3 }, backgroundColor: 'var(--pvta-bg)' }}>
                        {selectedAccount && (
                            <AccountEdit
                                initialAccount={selectedAccount}
                                allAccounts={userArray}
                                groupArray={groupArray}
                                updateAccount={updateAccount}
                                isPending={isPending}
                                refetchUserArray={refetchUserArray}
                            />
                        )}
                    </DialogContent>
                    <DialogActions
                        sx={{
                            justifyContent: 'flex-end',
                            gap: 1.5,
                            px: { xs: 2, md: 3 },
                            pt: 1.5,
                            pb: `calc(env(safe-area-inset-bottom) + 12px)`,
                            backgroundColor: 'rgba(248, 245, 239, 0.92)',
                            backdropFilter: 'blur(6px)',
                            borderTop: '1px solid var(--pvta-border)',
                            boxShadow: '0 -8px 24px rgba(20, 35, 25, 0.06)',
                        }}
                    >
                        {canImpersonate(selectedAccount, currentUserId, isSuperadmin()) && (
                            <Button
                                onClick={handleImpersonate}
                                disabled={isStarting}
                                variant="outlined"
                                startIcon={<SwitchAccountIcon />}
                                sx={{
                                    // Ganz an den linken Rand ziehen, damit man beim
                                    // Schließen/Speichern nicht versehentlich imitiert.
                                    mr: 'auto',
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    px: 2.5,
                                    color: GREEN_900,
                                    borderColor: 'var(--pvta-border)',
                                    '&:hover': {
                                        borderColor: GREEN_700,
                                        backgroundColor: 'var(--pvta-green-100)',
                                    },
                                }}
                            >
                                Imitieren
                            </Button>
                        )}

                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                borderRadius: 999,
                                fontWeight: 600,
                                px: 2.5,
                                color: GREEN_900,
                                borderColor: 'var(--pvta-border)',
                                '&:hover': {
                                    borderColor: GREEN_700,
                                    backgroundColor: 'var(--pvta-green-100)',
                                },
                            }}
                        >
                            Schließen
                        </Button>

                        {selectedAccount &&
                            (checkPermission(1, 2) ||
                                selectedAccount.id === currentUserId) && (
                                <SaveButton
                                    isPending={isPending}
                                    form="account-edit-form"
                                    type="submit"
                                />
                            )}
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default AccountsOverview;
